import { useEffect } from "react";
import { Lock, LogOut, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

/**
 * Hard block screen shown after login when the owner has manually locked
 * the company (companies.is_manually_locked = true). The user cannot
 * reach any ledger/settings page until the owner unlocks the account
 * from the admin panel. Logout is still allowed so the user can exit.
 *
 * Polls /auth/me every 15 s so when the owner unlocks the account the
 * user is redirected to the dashboard without a manual refresh.
 */
export default function AccountLocked() {
    const navigate = useNavigate();
    const { logout, refresh } = useAuth();

    useEffect(() => {
        const id = setInterval(async () => {
            try { await refresh(); } catch { /* ignore */ }
        }, 15000);
        return () => clearInterval(id);
    }, [refresh]);

    const doLogout = async () => {
        try { await logout(); } finally { navigate("/login", { replace: true }); }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-10" dir="rtl">
            <div className="w-full max-w-md rounded-2xl bg-white p-7 text-center shadow-sm ring-1 ring-neutral-200" data-testid="account-locked-screen">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-700">
                    <Lock className="h-8 w-8" strokeWidth={2.25} />
                </div>
                <h1 className="font-heading text-2xl font-bold text-neutral-900" data-testid="account-locked-title">
                    تم إقفال حسابك من قبل إدارة معيار.
                </h1>
                <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-200" data-testid="account-locked-reassure">
                    بياناتك وملفاتك محفوظة ولن يتم حذف أي شيء.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-neutral-600" data-testid="account-locked-contact">
                    يرجى التواصل مع إدارة معيار لإعادة فتح الحساب.
                </p>
                <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                    <Button
                        onClick={() => navigate("/support")}
                        className="flex-1 gap-2 bg-amber-700 text-white hover:bg-amber-800"
                        data-testid="account-locked-support-btn"
                    >
                        <Mail className="h-4 w-4" />
                        التواصل مع الدعم
                    </Button>
                    <Button
                        variant="outline"
                        onClick={doLogout}
                        className="flex-1 gap-2"
                        data-testid="account-locked-logout-btn"
                    >
                        <LogOut className="h-4 w-4" />
                        تسجيل الخروج
                    </Button>
                </div>
            </div>
        </div>
    );
}
